import { Box, Typography } from '@mui/material';
import { t } from 'i18next';
import { TrendData } from '../../../../common/dashboards/models/TrendData';
import { TrendDataScale } from '../../../../common/dashboards/models/TrendDataScale';
import { SEVERITY_COLORS, Severity, getLabel } from '../../../../common/enums/Severity';
import { TrendsChart } from '../../../../organization/dashboard/components/overview/trends/TrendsChart';
import { formatRange } from '../../../../organization/dashboard/utils';
import { SeverityChart } from '../../common/components/SeverityChart';

type Props = {
  trends: TrendData[];
  includePassed: boolean;
};

const PRINT_CONFIG = {
  width: 950,
  height: 225,
};

export const TrendsOverview = ({ trends, includePassed }: Props) => {
  const dataStats = trends.reduce(
    (stats, trend) => {
      const passed = includePassed ? stats.passed + trend.getPassedInstances() : 0;

      return {
        high: stats.high + trend.getHighInstances(),
        medium: stats.medium + trend.getMediumInstances(),
        low: stats.low + trend.getLowInstances(),
        passed,
      };
    },
    { high: 0, medium: 0, low: 0, passed: 0 }
  );

  const baseChartData = [
    { s: Severity.HIGH, v: dataStats.high },
    { s: Severity.MED, v: dataStats.medium },
    { s: Severity.LOW, v: dataStats.low },
  ];

  if (includePassed) {
    baseChartData.push({ s: Severity.PASSED, v: dataStats.passed });
  }

  let title = '';
  if (trends.length > 0) {
    const startDate = trends[0].getFrom();
    const endDate = trends[trends.length - 1].getTo();

    title = formatRange([startDate, endDate], TrendDataScale.DAY);
  }

  const chartData = baseChartData.map(({ s, v }) => ({
    name: getLabel(s),
    amount: v,
    color: SEVERITY_COLORS[s].main,
    textColor: SEVERITY_COLORS[s].text,
    backgroundColor: SEVERITY_COLORS[s].background,
  }));

  return (
    <Box sx={{ p: 2, overflow: 'hidden' }}>
      <Box sx={{ clear: 'both' }}>
        <Typography variant="overline" sx={{ fontWeight: 500, color: 'text.secondary', width: '70%', float: 'left' }}>
          {t('organizationDashboard.instanceSummary')}
        </Typography>
        <Typography variant="overline" sx={{ fontWeight: 500, color: 'text.secondary', width: '30%', float: 'right' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ clear: 'both', pt: 2 }}>
        <Box sx={{ width: '70%', float: 'left', fontSize: 'initial', '& *': { fontSize: 'initial' } }}>
          <TrendsChart trends={trends} includePassed={includePassed} onUpdateDateRange={() => {}} onSelectTrend={() => {}} printConfig={PRINT_CONFIG} />
        </Box>
        <Box sx={{ width: '30%', float: 'right' }}>
          <SeverityChart items={chartData} />
        </Box>
      </Box>
    </Box>
  );
};
